import { useEffect, Dispatch, SetStateAction } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { MemberRoleSelect } from "./MemberRoleSelect";

interface MemberDataFieldsProps {
  data: Record<string, any>;
  onChange: Dispatch<SetStateAction<Record<string, any>>>;
  unaId: string;
}

export const MemberDataFields = ({ data, onChange, unaId }: MemberDataFieldsProps) => {
  const updateField = (field: string, value: any) => {
    onChange(prev => ({ ...prev, [field]: value }));
  };

  // Set default values on mount using functional update to avoid race conditions
  useEffect(() => {
    onChange(prev => {
      const today = new Date().toISOString().split('T')[0];
      const updates: Record<string, any> = {};
      if (!prev.member_role) updates.member_role = "General Member";
      if (!prev.appointment_date) updates.appointment_date = today;
      return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
    });
  }, []);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label htmlFor="member_full_name">
            Full Legal Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="member_full_name"
            placeholder="John Michael Doe"
            value={data.member_full_name || ""}
            onChange={(e) => updateField("member_full_name", e.target.value)}
            required
          />
        </div>

        <div>
          <Label htmlFor="member_first_name">First Name</Label>
          <Input
            id="member_first_name"
            placeholder="John"
            value={data.member_first_name || ""}
            onChange={(e) => updateField("member_first_name", e.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="member_last_name">Last Name</Label>
          <Input
            id="member_last_name"
            placeholder="Doe"
            value={data.member_last_name || ""}
            onChange={(e) => updateField("member_last_name", e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="member_address">
          Residential Address <span className="text-destructive">*</span>
        </Label>
        <Input
          id="member_address"
          placeholder="123 Main Street"
          value={data.member_address || ""}
          onChange={(e) => updateField("member_address", e.target.value)}
          required
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="member_city">City</Label>
          <Input
            id="member_city"
            placeholder="Springfield"
            value={data.member_city || ""}
            onChange={(e) => updateField("member_city", e.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="member_state">State / Province</Label>
          <Input
            id="member_state"
            placeholder="IL"
            value={data.member_state || ""}
            onChange={(e) => updateField("member_state", e.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="member_zip">ZIP / Postal Code</Label>
          <Input
            id="member_zip"
            placeholder="62701"
            value={data.member_zip || ""}
            onChange={(e) => updateField("member_zip", e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="member_country">Country</Label>
        <Input
          id="member_country"
          placeholder="US"
          value={data.member_country || ""}
          onChange={(e) => updateField("member_country", e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="member_email">Email (Optional)</Label>
          <Input
            id="member_email"
            type="email"
            placeholder="john.doe@email.com"
            value={data.member_email || ""}
            onChange={(e) => updateField("member_email", e.target.value)}
          />
        </div>

        <div>
          <Label htmlFor="member_phone">Phone (Optional)</Label>
          <Input
            id="member_phone"
            type="tel"
            placeholder="(555) 123-4567"
            value={data.member_phone || ""}
            onChange={(e) => updateField("member_phone", e.target.value)}
          />
        </div>
      </div>

      <MemberRoleSelect
        value={data.member_role || "General Member"}
        onChange={(value) => updateField("member_role", value)}
        unaId={unaId}
      />

      <div>
        <Label htmlFor="appointment_date">
          Appointment Date <span className="text-destructive">*</span>
        </Label>
        <Input
          id="appointment_date"
          type="date"
          value={data.appointment_date || new Date().toISOString().split('T')[0]}
          onChange={(e) => updateField("appointment_date", e.target.value)}
          required
        />
      </div>

      <div>
        <Label htmlFor="membership_opening">
          Which place is open <span className="text-destructive">*</span>
        </Label>
        <Input
          id="membership_opening"
          placeholder="One general member seat"
          value={data.membership_opening || ""}
          onChange={(e) => updateField("membership_opening", e.target.value)}
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Say what place the Association is filling, in your own words.
        </p>
      </div>

      <div>
        <Label htmlFor="membership_opening_reason">Why it is open</Label>
        <Input
          id="membership_opening_reason"
          placeholder="The Association voted to add one member"
          value={data.membership_opening_reason || ""}
          onChange={(e) => updateField("membership_opening_reason", e.target.value)}
        />
      </div>

      <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
        This information will be used to pre-populate document templates in Phase 1.
      </div>
    </div>
  );
};
